import { useState } from 'react';
import { Tabs } from '../Tabs/Tabs';
import { TabsContent } from './TabsContent';
import { TabsWidgetContext } from './TabsWidgetContext';
import type { TabsWidgetProps } from './TabsContent';

export type { TabsWidgetProps };
export type { TabsContentProps } from './TabsContent';

// Re-export TabOption so consumers only need one import
export type { TabOption } from './TabsContent';

// ---------------------------------------------------------------------------
// TabsWidget
// ---------------------------------------------------------------------------

/**
 * Compound component that combines the `Tabs` nav bar with content panels.
 *
 * Each `TabsWidget.Content` child is unmounted when its value is not selected,
 * and mounted when it becomes active.
 *
 * Supports the same controlled/uncontrolled API as `Tabs`:
 *
 * ```tsx
 * // Uncontrolled
 * <TabsWidget options={tabs} defaultValue="active">
 *   <TabsWidget.Content value="active"><ActivePanel /></TabsWidget.Content>
 *   <TabsWidget.Content value="completed"><CompletedPanel /></TabsWidget.Content>
 * </TabsWidget>
 *
 * // Controlled (e.g. TanStack Router)
 * <TabsWidget options={tabs} value={tab} onChange={(v) => navigate(...)}>
 *   <TabsWidget.Content value="active"><ActivePanel /></TabsWidget.Content>
 *   <TabsWidget.Content value="completed"><CompletedPanel /></TabsWidget.Content>
 * </TabsWidget>
 * ```
 */
function TabsWidgetRoot({ options, value, defaultValue, onChange, children }: TabsWidgetProps) {
  const isControlled = value !== undefined;

  const [internalValue, setInternalValue] = useState<string>(
    () => defaultValue ?? options[0]?.value ?? ''
  );

  const selectedValue = isControlled ? value : internalValue;

  function handleChange(tabValue: string) {
    if (!isControlled) {
      setInternalValue(tabValue);
    }
    onChange?.(tabValue);
  }

  return (
    <TabsWidgetContext.Provider value={{ selectedValue }}>
      <Tabs options={options} value={selectedValue} onChange={handleChange} />
      {children}
    </TabsWidgetContext.Provider>
  );
}

// Attach Content as a static property for the compound pattern
TabsWidgetRoot.Content = TabsContent;

export const TabsWidget = TabsWidgetRoot;
